import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { DriverActivityComponent } from '../../driver-activity/driver-activity.component';
import { OperationStatus } from '../../../models/operation-status.model';
import { DriverOperationsService, FilterService } from '../../../../../_shared/services';
import { PaginationRequest } from '../../../../../_shared/models/pagination-request.model';
import { endOfDay, startOfDay } from 'date-fns';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { debounceTime, Observable, Subject, Subscription } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-drivers-in-yard-modal',
  standalone: true,
  imports: [DriverActivityComponent, TranslocoPipe],
  templateUrl: './drivers-in-yard-modal.component.html',
  styleUrl: './drivers-in-yard-modal.component.scss',
})
export class DriversInYardModalComponent implements OnInit, OnDestroy {
  @Input() isModal: boolean = false;
  @Input() componentId: string = '';
  @Output() toggleExpandEvent = new EventEmitter<string>();
  @Input() search$: Observable<string>;

  public title: string = '';
  public count: number = 0;
  public columnHeaders: string[] = [];
  public filteredDriversData: OperationStatus[] = [];
  public driversData: OperationStatus[] = [];
  public isLoading: boolean = true;
  public searchTerm: string = '';
  public isFetching: boolean = false;
  public hasError: boolean = false;
  public currentDate: Date = new Date();
  public paginationRequest: PaginationRequest = {
    accountIds: [],
    timeFrom: startOfDay(this.currentDate),
    timeTo: endOfDay(this.currentDate),
    page: 1,
    pageSize: 15,
    searchTerm: ''
  };

  private operationStatusService = inject(DriverOperationsService);
  private filterService = inject(FilterService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);
  private hasMoreData: boolean = true;
  private filterSubscription: Subscription;
  private isExpanded: boolean = false;
  private cancelSubject = new Subject<void>();
  private searchSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.initializeDriversValues();
    this.setupFilterSubscription();
    this.setupSearchSubscription();
  }

  private initializeDriversValues(): void {
    this.columnHeaders = [this.transloco.translate('driverDashboard.table.driver'), this.transloco.translate('driverDashboard.table.vehicle'), this.transloco.translate('driverDashboard.columns.timeInYard'), this.transloco.translate('driverDashboard.columns.location')];
  }

  private setupFilterSubscription(): void {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe((filter) => {
      if (!filter || Object.values(filter).every(x => x == null)) {
        return;
      }

      this.driversData = [];
      this.hasMoreData = true;
      this.filteredDriversData = [];
      this.hasError = false;
      this.paginationRequest.accountIds = filter.accountIds;
      this.onFilterChangeAsync();
    });
  }

  private setupSearchSubscription(): void {
    if (this.search$) {
      this.searchSubscription = this.search$.pipe(
        debounceTime(1000)
      ).subscribe(searchTerm => {
        this.isFetching = false;
        this.searchTerm = searchTerm;
        this.isLoading = true;
        this.hasError = false;
        this.filterDriversAsync(searchTerm);
      });
    }
  }

  async getDriversInYardAsync() {
    if (this.isFetching || !this.hasMoreData) {
      return;
    }

    this.isLoading = true;
    this.isFetching = true;
    this.hasError = false;
    try {
      this.cancelSubject.next();
      const response = await this.operationStatusService.getDriversInYard(this.paginationRequest, this.cancelSubject);
      const inYardDrivers = response.data?.results ?? [];
      if (inYardDrivers.length > 0) {
        this.driversData = [...this.driversData, ...inYardDrivers];
        this.filteredDriversData = [...this.driversData];
        this.count = response.data?.totalCount ?? 0;
        this.hasMoreData = this.driversData.length < response.data?.totalCount!;
      } else if (!inYardDrivers.length) {
        this.driversData = inYardDrivers;
        this.filteredDriversData = this.driversData;
        this.count = response.data?.totalCount ?? 0;
      } else {
        this.hasMoreData = false;
      }

      this.isLoading = false;
      this.isFetching = false;
    } catch (error: any) {
      this.hasError = true;
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
          this.isFetching = false;
        }
      });
    }
  }

  protected async onFilterChangeAsync(): Promise<void> {
    await this.getDriversInYardAsync();
  }

  async onScroll(): Promise<void> {
    if (this.hasMoreData && !this.isFetching) {
      this.paginationRequest.page += 1;
      await this.getDriversInYardAsync();
    }
  }

  async onExpand(event: any): Promise<void> {
    this.isExpanded = !this.isExpanded;

    // Reset pagination and data
    this.hasMoreData = true;
    this.isLoading = true;
    this.hasError = false;
    this.driversData = [];
    this.paginationRequest.page = 1;
    this.paginationRequest.pageSize = this.isExpanded ? 20 : 5;
    this.isFetching = false;
    this.paginationRequest.searchTerm = '';
    this.cancelSubject.next();
    this.toggleExpandEvent.emit(event);
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async filterDriversAsync(searchTerm: string): Promise<void> {
    this.paginationRequest.searchTerm = searchTerm ?? '';
    this.paginationRequest.page = 1;
    const previousCount = this.count;
    this.driversData = [];
    this.hasMoreData = true;
    await this.getDriversInYardAsync();
    this.count = previousCount; /// this is for displaying total drivers in yard
    const searchLower = this.normalizeSearchTerm(this.searchTerm);
    const tokens = searchLower.split(' ').filter(Boolean);
    this.filteredDriversData = this.driversData.filter(driver => {
      const fullName = this.normalizeSearchTerm(`${driver.firstName} ${driver.lastName}`);
      return tokens.every(t => fullName.includes(t));
    });
  }

  private normalizeSearchTerm(searchTerm: string): string {
    return searchTerm.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
}